import { createContext, forwardRef, useContext, useId } from "react";
import { cn } from "@/lib/cn";

/**
 * RadioGroup — controlled native radio asosida (Faza 8 polish).
 *
 * Pattern:
 *   <RadioGroup value={v} onValueChange={setV} name="plan">
 *     <RadioItem value="free" label="Free" description="0 so'm/oy" />
 *     <RadioItem value="pro" label="Pro" description="299 ming so'm/oy" />
 *   </RadioGroup>
 *
 * UX:
 *   - Checked outer ring (violet 2px), inner dot violet
 *   - Card variant (orientation="card") — har item Card-uslub border bilan
 *   - Inline list variant (default) — fitr/oddiy radio
 *
 * Token'lar: --radio-* (tokens.css)
 */
interface RadioCtx {
  value?: string;
  name: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  variant: "list" | "card";
  size: "sm" | "md";
}

const RadioContext = createContext<RadioCtx | null>(null);

export interface RadioGroupProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  name?: string;
  disabled?: boolean;
  variant?: "list" | "card";
  size?: "sm" | "md";
  orientation?: "vertical" | "horizontal";
}

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      className,
      value,
      onValueChange,
      name,
      disabled,
      variant = "list",
      size = "md",
      orientation = "vertical",
      children,
      ...props
    },
    ref,
  ) => {
    const generated = useId();
    const groupName = name ?? `rg-${generated}`;
    return (
      <RadioContext.Provider
        value={{
          value,
          name: groupName,
          onChange: (v) => onValueChange?.(v),
          disabled,
          variant,
          size,
        }}
      >
        <div
          ref={ref}
          role="radiogroup"
          className={cn(
            "flex",
            orientation === "vertical" ? "flex-col gap-2" : "flex-row gap-3 flex-wrap",
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </RadioContext.Provider>
    );
  },
);
RadioGroup.displayName = "RadioGroup";

export interface RadioItemProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "name" | "value" | "onChange" | "size"> {
  value: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
  /** Card variantida o'ng tomon meta (badge, narx) */
  trailing?: React.ReactNode;
}

export const RadioItem = forwardRef<HTMLInputElement, RadioItemProps>(
  ({ className, value, label, description, trailing, disabled, id, ...props }, ref) => {
    const ctx = useContext(RadioContext);
    if (!ctx) throw new Error("RadioItem RadioGroup ichida bo'lishi shart");

    const reactId = useId();
    const inputId = id ?? `radio-${reactId}`;
    const checked = ctx.value === value;
    const isDisabled = disabled || ctx.disabled;

    const sizeBox = ctx.size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
    const sizeDot = ctx.size === "sm" ? "h-1.5 w-1.5" : "h-2 w-2";

    const visual = (
      <span
        aria-hidden
        className={cn(
          "relative inline-flex shrink-0 items-center justify-center rounded-full border transition-all duration-150",
          sizeBox,
          "bg-[var(--radio-bg)]",
          checked
            ? "border-[var(--radio-checked-border)] shadow-[var(--radio-checked-glow)]"
            : "border-[var(--radio-border)] group-hover:border-[var(--checkbox-border-hover)]",
        )}
      >
        <span
          className={cn(
            "rounded-full bg-[var(--radio-checked-dot)] transition-transform duration-150",
            sizeDot,
            checked ? "scale-100" : "scale-0",
          )}
        />
      </span>
    );

    const input = (
      <input
        ref={ref}
        id={inputId}
        type="radio"
        name={ctx.name}
        value={value}
        checked={checked}
        disabled={isDisabled}
        onChange={() => ctx.onChange(value)}
        className="peer sr-only"
        {...props}
      />
    );

    if (ctx.variant === "card") {
      return (
        <label
          htmlFor={inputId}
          className={cn(
            "group flex flex-1 items-start gap-3 rounded-xl border p-3 cursor-pointer transition-all duration-150",
            checked
              ? "border-[var(--radio-checked-border)] bg-[var(--accent-violet-soft)] shadow-[var(--glow-sm)]"
              : "border-[var(--border-subtle)] bg-[var(--bg-surface)] hover:border-[var(--border-strong)]",
            isDisabled && "cursor-not-allowed opacity-60",
            "focus-within:ring-2 focus-within:ring-[var(--accent-violet)]/40",
            className,
          )}
        >
          {input}
          <span className="mt-0.5">{visual}</span>
          <span className="flex flex-1 flex-col gap-0.5 min-w-0">
            {label && (
              <span className="text-sm font-medium text-[var(--text-primary)] leading-tight">
                {label}
              </span>
            )}
            {description && (
              <span className="text-xs text-[var(--text-secondary)] leading-snug">
                {description}
              </span>
            )}
          </span>
          {trailing && <span className="shrink-0">{trailing}</span>}
        </label>
      );
    }

    // List variant
    return (
      <label
        htmlFor={inputId}
        className={cn(
          "group inline-flex items-start gap-2.5 cursor-pointer select-none",
          isDisabled && "cursor-not-allowed opacity-60",
          className,
        )}
      >
        {input}
        <span className="mt-0.5">{visual}</span>
        <span className="flex flex-col gap-0.5">
          {label && (
            <span className="text-sm font-medium text-[var(--text-primary)] leading-tight">
              {label}
            </span>
          )}
          {description && (
            <span className="text-xs text-[var(--text-secondary)] leading-snug">
              {description}
            </span>
          )}
        </span>
      </label>
    );
  },
);
RadioItem.displayName = "RadioItem";
